import { useState, useEffect } from "react";
import { Coins } from "lucide-react";
import api from "../../lib/api";

// Blended € per credit (Pro plan baseline, same value used server-side in
// core/ai_margin.py). Keep the two files in sync when pricing changes.
const EUR_PER_CREDIT = 0.037;

// In-memory cache shared across all chip instances so we only hit the
// rules endpoint once per session/restaurant.
const rulesCache = {};

async function loadRules(restaurantId) {
  if (rulesCache[restaurantId]) return rulesCache[restaurantId];
  try {
    const data = await api.request(`/subscription/credits/${restaurantId}`);
    const rules = data?.rules || {};
    rulesCache[restaurantId] = rules;
    return rules;
  } catch {
    return {};
  }
}

/**
 * Small inline badge that shows "N créditos · ~X,XX€" for a given AI action.
 * Self-fetches the restaurant's credit rules and caches them per-session.
 *
 * Usage:
 *   <PriceChip restaurantId={rid} action="analytics_ask" />
 */
export function PriceChip({ restaurantId, action, compact = false, className = "" }) {
  const [cost, setCost] = useState(null);

  useEffect(() => {
    if (!restaurantId || !action) return;
    let alive = true;
    (async () => {
      const rules = await loadRules(restaurantId);
      if (alive && rules[action] != null) setCost(rules[action]);
    })();
    return () => { alive = false; };
  }, [restaurantId, action]);

  if (cost == null) return null;

  const eur = (cost * EUR_PER_CREDIT).toFixed(2).replace(".", ",");

  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium
                  bg-amber-500/10 border border-amber-500/30 text-amber-700
                  dark:text-amber-300 px-1.5 py-0.5 rounded-full ${className}`}
      data-testid={`price-chip-${action}`}
      title={`Esta acción consume ${cost} crédito(s) de IA (~${eur}€ coste equivalente)`}
    >
      <Coins className="w-2.5 h-2.5" />
      {cost} {compact ? "cr" : cost === 1 ? "crédito" : "créditos"}
      {!compact && <span className="opacity-70">· ~{eur}€</span>}
    </span>
  );
}

export default PriceChip;
